/**
 * Client for the Nexus /external/authorize call.
 *
 * Important: this client does NOT talk to Nexus directly. The Nexus X-App-Key
 * cannot ship in a public bundle, so the frontend talks to our BFF — a Cloud
 * Function in this project that holds the key and proxies the call. See
 * `functions/index.js` for the BFF and `firebase.json` for the
 * `/api/authorize` rewrite.
 *
 * In Vite dev (localhost:5173), the `/api` path is proxied to the deployed
 * BFF via vite.config.ts so the same relative URL works in both environments.
 */

export interface AuthorizationDecision {
  allowed: boolean;
  reason: string | null;
  message: string | null;
  uid: string | null;
  email: string | null;
  display_name: string | null;
  org_id: string | null;
  nexus_roles: string[];
  app_roles: string[];
  /**
   * Intersection of this app's `required_permissions` and the signed-in
   * user's effective permissions. Empty `[]` when the app still uses
   * legacy `allowed_roles`. Prefer this over `app_roles` for new code.
   */
  granted_permissions: string[];
  team_ids: string[];
  client_id: string | null;
  profile: {
    job_title: string | null;
    department: string | null;
    photo_url: string | null;
  } | null;
  cache_ttl_seconds: number;
  issued_at: string | null;
  expires_at: string | null;
}

const BFF_URL = '/api/authorize';

type CacheEntry = { expiresAt: number; decision: AuthorizationDecision };
const cache = new Map<string, CacheEntry>();

async function hashToken(token: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function authorize(idToken: string): Promise<AuthorizationDecision> {
  const key = await hashToken(idToken);
  const cached = cache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.decision;
  }

  const res = await fetch(BFF_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken }),
  });

  if (res.status === 401) {
    cache.delete(key);
    throw new Error('Nexus rejected the credentials (401). Re-authenticate.');
  }
  if (!res.ok) {
    throw new Error(`Authorize failed: HTTP ${res.status}`);
  }

  const decision = (await res.json()) as AuthorizationDecision;
  const ttl = Math.max(0, Number(decision.cache_ttl_seconds) || 60);
  cache.set(key, { expiresAt: Date.now() + ttl * 1000, decision });
  return decision;
}

export function clearAuthorizationCache(): void {
  cache.clear();
}
